import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import type {
  PollerRun,
  PollerRunFilterBreakdown,
  PollerRunOutcome,
  PollerRunsSummary,
} from '@app/shared';
import {
  Cluster,
  Disclosure,
  Heading,
  Link,
  Spinner,
  StatusBadge,
  Stack,
  Surface,
  Text,
  type StatusBadgeProps,
} from '../ui';
import { usePageActive } from '../lib/pageActivity';
import { api, ApiError } from '../lib/api';
import { formatDuration } from '../lib/duration';
import { PollerConnectionsTable } from './PollerConnectionsTable';
import { PollerDecisionsTable } from './PollerDecisionsTable';

const POLLER_RUNS_QUERY_KEY = ['admin', 'poller-runs'] as const;
const POLLER_RUN_DETAIL_QUERY_KEY = (id: string) => ['admin', 'poller-run-detail', id] as const;
/** Light background refresh so the "recent activity" view stays roughly live. */
const REFETCH_MS = 30_000;

/**
 * Operator-only "Email poller — recent activity" section (v1.13.5, §17.1; #306). Renders a
 * cadence headline, an aggregate picked-up/forwarded/filtered headline over the shown window,
 * and one expandable row per recent poller tick. Each tick shows **Picked up · Forwarded ·
 * Filtered**, and Filtered expands to the reason breakdown (self/sent, no-reply, list-mail,
 * calendar-auto, auto-submitted, bulk, delivery-report, no-matching-agent, duplicate) so an
 * operator can self-diagnose "my inbox agent gets no messages": mail is either not ingested,
 * ingested-then-filtered (and by which rule), or ingested with no agent subscribed.
 *
 * Gating mirrors PollerConfigSection: the operator status isn't in the auth payload, so we
 * just attempt the read. A non-operator's GET returns 403 (401 anonymous) — in that case we
 * render NOTHING (not even the heading), so normal admins never see a forbidden section.
 */
export function PollerRunsSection() {
  const pageActive = usePageActive();
  const query = useQuery({
    queryKey: POLLER_RUNS_QUERY_KEY,
    queryFn: () => api.getPollerRuns(),
    retry: false,
    // Keep the view live while someone is using it; stop polling once the read is
    // forbidden (a non-operator's browser mustn't re-request a 403 on a timer) and while
    // the page is hidden/idle (a tab left open would keep the scale-to-zero DB awake).
    refetchInterval: (q) => (q.state.error || !pageActive ? false : REFETCH_MS),
  });

  const status = query.error instanceof ApiError ? query.error.status : undefined;
  if (status === 403 || status === 401) return null;
  // Until the gate resolves, render nothing so non-operators never flash the heading.
  if (query.isPending || !query.data) return null;

  const { runs, summary } = query.data;

  return (
    <Surface bordered radius="lg" padding="5" data-testid="poller-runs-section">
      <Stack gap="4">
        <Cluster gap="3" align="start" justify="between" wrap>
          <Stack gap="1" style={{ flex: '1 1 20rem', minWidth: 0 }}>
            <Heading level={2} size="md">
              Email poller — recent activity
            </Heading>
            <Text size="sm" tone="secondary" data-testid="poller-runs-status">
              {headline(summary)}
            </Text>
            <Text size="sm" tone="secondary" data-testid="poller-runs-aggregate">
              {aggregateHeadline(runs)}
            </Text>
          </Stack>
          <Link
            as={RouterLink}
            // Typed router props via the same cast the app's teaching empty state uses.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ to: '/app/admin/poller-activity' } as any)}
            data-testid="poller-activity-link"
          >
            Look up a user&apos;s activity →
          </Link>
        </Cluster>

        {runs.length === 0 ? (
          <Text size="sm" tone="muted">
            No checks recorded yet — the poller writes a row each time it runs.
          </Text>
        ) : (
          <Stack gap="2" role="list">
            {runs.map((r) => (
              <PollerRunRow key={r.id} run={r} />
            ))}
          </Stack>
        )}
      </Stack>
    </Surface>
  );
}

/** One recent tick: a bordered row that expands to reveal the filtered-reason breakdown. */
function PollerRunRow({ run }: { run: PollerRun }) {
  const [open, setOpen] = useState(false);
  const meta = OUTCOME_META[run.outcome];
  const pickedUp = run.emailsIngested;
  const forwarded = run.runsSpawned;
  const filtered = filteredTotal(run);

  return (
    <Surface bordered radius="md" padding="3" role="listitem" data-testid="poller-run-row">
      <Disclosure
        open={open}
        onToggle={() => setOpen((v) => !v)}
        chevronAtEnd
        bodyTestId="poller-run-reasons"
        summary={
          <Cluster gap="3" align="center" wrap style={{ flex: '1 1 auto', minWidth: 0 }}>
            <StatusBadge size="sm" tone={run.ok ? meta.tone : 'danger'} label={meta.label} />
            <Text
              as="span"
              size="sm"
              tone="secondary"
              title={new Date(run.startedAt).toLocaleString()}
            >
              {relativeTime(run.startedAt)}
            </Text>
            <Text as="span" size="sm" data-testid="poller-run-counts">
              Picked up {pickedUp} · Forwarded {forwarded} · Filtered {filtered}
            </Text>
            {formatDuration(run.durationMs) && (
              <Text as="span" size="sm" tone="muted">
                {formatDuration(run.durationMs)}
              </Text>
            )}
          </Cluster>
        }
      >
        <Stack gap="4" style={{ paddingTop: '0.75rem', paddingLeft: '1.5rem' }}>
          <Stack gap="1">
            <Text size="xs" tone="muted" weight="semibold">
              Filtered — reason breakdown
            </Text>
            {reasonLines(run)}
          </Stack>
          {open && <PollerRunDetail runId={run.id} />}
        </Stack>
      </Disclosure>
    </Surface>
  );
}

/**
 * The per-user drill-down for one open tick (#318): its per-connection fetch rollups and its
 * per-email decisions, fetched LAZILY (only when the row is expanded) from the operator-gated
 * `GET /poller-runs/:id`. Kept a separate component so the query mounts/unmounts with the row.
 */
function PollerRunDetail({ runId }: { runId: string }) {
  const query = useQuery({
    queryKey: POLLER_RUN_DETAIL_QUERY_KEY(runId),
    queryFn: () => api.getPollerRunDetail(runId),
    retry: false,
  });

  if (query.isPending) {
    return (
      <Cluster gap="2" align="center" data-testid="poller-run-detail-loading">
        <Spinner size="sm" />
        <Text size="sm" tone="muted">
          Loading run details…
        </Text>
      </Cluster>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Text size="sm" tone="danger" data-testid="poller-run-detail-error">
        Could not load this tick&apos;s details.
      </Text>
    );
  }

  const { connections, decisions } = query.data;
  return (
    <Stack gap="4" data-testid="poller-run-detail">
      <Stack gap="2">
        <Text size="xs" tone="muted" weight="semibold">
          Connections polled ({connections.length})
        </Text>
        <PollerConnectionsTable
          connections={connections}
          hideConnection={false}
          caption="Per-connection fetch diagnostics for this poller tick"
          emptyLabel="No connections were polled in this tick."
        />
      </Stack>
      <Stack gap="2">
        <Text size="xs" tone="muted" weight="semibold">
          Emails ({decisions.length})
        </Text>
        <PollerDecisionsTable
          decisions={decisions}
          caption="Per-email decisions for this poller tick"
          emptyLabel="No emails were ingested in this tick."
        />
      </Stack>
    </Stack>
  );
}

/** The reason breakdown body for one tick's Filtered count. */
function reasonLines(run: PollerRun) {
  if (!run.filterBreakdown) {
    return (
      <Text size="sm" tone="muted">
        Reason breakdown not recorded for this check.
      </Text>
    );
  }
  const rows = REASON_META.map((m) => ({
    label: m.label,
    count: run.filterBreakdown![m.key],
  })).filter((r) => r.count > 0);
  if (rows.length === 0) {
    return (
      <Text size="sm" tone="muted">
        Nothing filtered this check.
      </Text>
    );
  }
  return rows.map((r) => (
    <Cluster key={r.label} gap="2" align="center" justify="between">
      <Text as="span" size="sm" tone="secondary">
        {r.label}
      </Text>
      <Text as="span" size="sm">
        {r.count}
      </Text>
    </Cluster>
  ));
}

/** "Checking every 5 min · active hours 09:00–17:00 (Europe/London) · last check 2m ago" */
function headline(summary: PollerRunsSummary): string {
  const { config, lastRunAt } = summary;
  const parts: string[] = [`Checking every ${config.checkFrequencyMinutes} min`];
  parts.push(
    config.activeHoursEnabled
      ? `active hours ${config.activeHoursStart}–${config.activeHoursEnd} (${config.activeHoursTimezone})`
      : 'polling 24/7',
  );
  parts.push(lastRunAt ? `last check ${relativeTime(lastRunAt)}` : 'no checks yet');
  return parts.join(' · ');
}

/** Aggregate picked-up/forwarded/filtered across the shown ticks. */
function aggregateHeadline(runs: PollerRun[]): string {
  const pickedUp = runs.reduce((n, r) => n + r.emailsIngested, 0);
  const forwarded = runs.reduce((n, r) => n + r.runsSpawned, 0);
  const filtered = runs.reduce((n, r) => n + filteredTotal(r), 0);
  const checks = runs.length === 1 ? '1 check' : `${runs.length} checks`;
  return `Picked up ${pickedUp} · Forwarded ${forwarded} · Filtered ${filtered} over the last ${checks}`;
}

/**
 * Filtered total for one tick = the sum of the #306 breakdown buckets when present. For a
 * pre-#306 row (null breakdown) the only historical filter signal is the non-actionable
 * auto-skip count, so fall back to that.
 */
function filteredTotal(run: PollerRun): number {
  const b = run.filterBreakdown;
  if (!b) return run.nonActionableSkipped;
  return (
    b.selfSuppressed +
    b.noreplySender +
    b.calendarAuto +
    b.listMail +
    b.autoSubmitted +
    b.bulkPrecedence +
    b.deliveryReport +
    b.noMatchingTrigger +
    b.duplicates
  );
}

const REASON_META: { key: keyof PollerRunFilterBreakdown; label: string }[] = [
  { key: 'selfSuppressed', label: 'Self / sent' },
  { key: 'noreplySender', label: 'No-reply sender' },
  { key: 'listMail', label: 'List mail' },
  { key: 'calendarAuto', label: 'Calendar auto' },
  { key: 'autoSubmitted', label: 'Auto-submitted' },
  { key: 'bulkPrecedence', label: 'Bulk' },
  { key: 'deliveryReport', label: 'Delivery report' },
  { key: 'noMatchingTrigger', label: 'No matching agent' },
  { key: 'duplicates', label: 'Duplicate' },
];

const OUTCOME_META: Record<PollerRunOutcome, { tone: StatusBadgeProps['tone']; label: string }> = {
  polled: { tone: 'success', label: 'Polled' },
  idle_no_agents: { tone: 'neutral', label: 'Idle — no agents' },
  idle_off_hours: { tone: 'neutral', label: 'Idle — off hours' },
  error: { tone: 'danger', label: 'Error' },
};

/** Compact "N{s,m,h,d} ago" from an ISO instant (no live ticker; recomputed each render). */
function relativeTime(iso: string, now: number = Date.now()): string {
  const diffMs = now - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diffMs / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
